import React, { useEffect, useRef, useState } from 'react';

/**
 * Reveals its children once they scroll into view, using the shared
 * entrance curve. Falls back to visible when IntersectionObserver is
 * unavailable, so content is never trapped behind a missing API.
 * @author Nelson
 */
const Reveal = ({ as: Tag = 'div', delay = 0, className = '', children, ...rest }) => {
    const ref = useRef(null);
    const [shown, setShown] = useState(false);

    useEffect(() => {
        const node = ref.current;

        if (!node || typeof IntersectionObserver === 'undefined') {
            setShown(true);
            return undefined;
        }

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries.some((entry) => entry.isIntersecting)) {
                    setShown(true);
                    observer.disconnect();
                }
            },
            { rootMargin: '0px 0px -10% 0px', threshold: 0.06 }
        );

        observer.observe(node);

        return () => observer.disconnect();
    }, []);

    const classes = ['reveal-on-view', shown ? 'is-in' : '', className]
        .filter(Boolean)
        .join(' ');

    return (
        <Tag ref={ref} className={classes} style={{ '--reveal-delay': `${delay}ms` }} {...rest}>
            {children}
        </Tag>
    );
};

/**
 * Splits a heading into per-word spans so the entrance can stagger across
 * the line. Whitespace is preserved so the text remains selectable and
 * screen readers still announce the original sentence.
 */
export const SplitWords = ({ text, className = '', start = 0, step = 58 }) => {
    const words = text.split(' ');

    return (
        <span className={className}>
            {words.map((word, index) => (
                <React.Fragment key={`${word}-${index}`}>
                    <span className="word">
                        <span
                            className="word__inner"
                            style={{ '--reveal-delay': `${start + index * step}ms` }}
                        >
                            {word}
                        </span>
                    </span>
                    {index < words.length - 1 ? ' ' : ''}
                </React.Fragment>
            ))}
        </span>
    );
};

export default Reveal;
